import { DeleteIcon, TickIcon } from "../../../../atoms/CustomIcons";
import { useState, useMemo, useEffect } from "react";
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Image,
  Popover,
  Progress,
  Tooltip,
} from "antd";

import { useAppDispatch } from "../../../../config/hooks";
import {
  getPotentialDealDetails,
  getProposalListing,
} from "../../ducks/actions";
import { useNavigate } from "react-router-dom";
import { MoreOutlined } from "@ant-design/icons";
import ConfirmDelete from "../../../../atoms/ConfirmDelete";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../../../atoms/Toasts/ErrorMessage";
import { SuccessMessage } from "../../../../atoms/Toasts/SuccessMessage";
import {
  changeProposalStatus,
  deleteProposalById,
  makeProposalAsCopied,
} from "../../ducks/services";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import PendingApproval from "../CardStatus/PendingApproval";
import InProgress from "../CardStatus/InProgress";
import Ready from "../CardStatus/Ready";
import Sent from "../CardStatus/Sent";
import Accepted from "../CardStatus/Accepted";
import Revision from "../CardStatus/Revision";
import Declined from "../CardStatus/Declined";
import Archived from "../CardStatus/Archived";
import { formatRelativeTime } from "../../../../atoms/TimeFormatDisplay";
import { PopUp } from "../../../../atoms/PopUp";
import { GetDealType, TableRecordProps } from "../../ducks/types";
import InfoBox from "../../../../atoms/InfoBox";
import FileDragger from "../../../CompanySettings/CrewYards/BulkUpload/Components/FileDragger";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

interface Proposal {
  id: string;
  name: string; // This will be the original name, potentially the date string
  summary?: string;
  modified_date: string;
  gross_margin_perc: number;
  total_amount: number;
  status: string;
  document?: [];
}

interface FormattedProposal extends Proposal {
  display_name: string;
}

interface PropsType {
  setLoading: (loading: boolean) => void;
  loading: boolean;
  proposalListData: [] | null;
  dealsDetailData: GetDealType | null;
}

const formatProposalsForDisplay = (
  proposals: Proposal[]
): FormattedProposal[] => {
  if (!proposals || proposals.length === 0) {
    return [];
  }

  const proposalsByDate = new Map<string, Proposal[]>();

  proposals.forEach((proposal) => {
    const dateKey = proposal?.name;
    if (!proposalsByDate.has(dateKey)) {
      proposalsByDate.set(dateKey, []);
    }
    proposalsByDate.get(dateKey)?.push(proposal);
  });

  const formattedProposals: FormattedProposal[] = [];

  proposals.forEach((proposal) => {
    const dateKey = proposal?.name;
    const dailyProposals = proposalsByDate.get(dateKey) || [];
    const index = dailyProposals.indexOf(proposal);

    if (dailyProposals.length === 1) {
      formattedProposals.push({
        ...proposal,
        display_name: `Proposal - ${proposal.name}`,
      });
    } else {
      const formattedDate = dateKey;
      const versionName = `Proposal - ${formattedDate} - ${
        dailyProposals.length - index
      }`;
      formattedProposals.push({
        ...proposal,
        display_name: versionName,
      });
    }
  });

  return formattedProposals;
};
interface FileType {
  name: string;
  type: string;
  size: number;
}
export default function ProposalCards(props: PropsType) {
  const { setLoading, loading, proposalListData, dealsDetailData } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEstimateIncomplete, setIsEstimateIncomplete] = useState(false);
  const [isProposalIncomplete, setIsProposalIncomplete] = useState(false);
  const [changeStatusText, setChangeStatusText] = useState("");
  const [openChangeSentPopover, setOpenChangeSentPopover] = useState(false);
  const [openChangeAcceptedPop, setOpenChangeAcceptedPop] = useState(false);
  const [openSignaturePopover, setOpenSignaturePopover] = useState(false);
  const [replaceOldAccepted, setReplaceOldAccepted] = useState(false);
  const [oldAcceptProposal, setOldAcceptProposal] =
    useState<FormattedProposal | null>(null);
  const [checklistFile, setChecklistFile] = useState<FileType | null>(null);
  const [tableRecord, setTableRecord] = useState<TableRecordProps | null>(null);
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const dealId = pathSegments[pathSegments.length - 2];

  const handleStatusChange = (status: string) => {
    setLoading(true);

    if (
      status !== "in_progress" &&
      status !== "approval_pending" &&
      status !== "ready" &&
      (!dealsDetailData?.start_date ||
        !dealsDetailData?.end_date ||
        !dealsDetailData?.billing ||
        !dealsDetailData?.net_terms)
    ) {
      setLoading(false);
      setIsProposalIncomplete(true);
      return;
    }

    if (tableRecord) {
      const payload = {
        status,
        deal_id: dealId,
      };
      changeProposalStatus(payload, tableRecord?.id)
        .then(() => {
          setLoading(false);
          dispatch(getPotentialDealDetails(dealId));
          dispatch(
            getProposalListing({ company_id: companyId, deal_id: dealId })
          );
          setTableRecord(null);
          setChangeStatusText("");
          setOpenChangeSentPopover(false);
          setOpenChangeAcceptedPop(false);
          setOpenSignaturePopover(false);
          setReplaceOldAccepted(false);
          SuccessMessage("Status changed");
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const checkStatus = (status: string) => {
    if (tableRecord?.status === status) return;
    setChangeStatusText(status);
    const checkOldAccepted = proposalListData?.some(
      (proposal: Proposal) =>
        proposal.status === "accepted" && proposal.id !== tableRecord?.id
    );
    if (tableRecord?.status === "sent" && status !== "accepted") {
      setOpenChangeSentPopover(true);
    } else if (tableRecord?.status === "accepted" && status !== "accepted") {
      setOpenChangeAcceptedPop(true);
    } else if (checkOldAccepted && status === "accepted") {
      setReplaceOldAccepted(true);
    }
    // else if (tableRecord?.status !== "accepted" && status === "accepted") {
    //   setOpenSignaturePopover(true);
    // }
    else {
      handleStatusChange(status);
    }
  };

  const changeOldAcceptedProposal = (status: string) => {
    if (oldAcceptProposal) {
      setLoading(true);

      if (
        status !== "in_progress" &&
        status !== "approval_pending" &&
        status !== "ready" &&
        (!dealsDetailData?.start_date ||
          !dealsDetailData?.end_date ||
          !dealsDetailData?.billing ||
          !dealsDetailData?.net_terms)
      ) {
        setLoading(false);
        setIsProposalIncomplete(true);
        return;
      }

      const payload = {
        status: "declined",
        deal_id: dealId,
      };
      changeProposalStatus(payload, oldAcceptProposal?.id)
        .then(() => {
          // setLoading(false);

          if (tableRecord) {
            const secondPayload = {
              status,
              deal_id: dealId,
            };
            changeProposalStatus(secondPayload, tableRecord?.id)
              .then(() => {
                setLoading(false);
                dispatch(getPotentialDealDetails(dealId));
                dispatch(
                  getProposalListing({ company_id: companyId, deal_id: dealId })
                );
                setTableRecord(null);
                setChangeStatusText("");
                setOpenChangeSentPopover(false);
                setOpenChangeAcceptedPop(false);
                setOpenSignaturePopover(false);
                setReplaceOldAccepted(false);
                SuccessMessage("Status changed");
              })
              .catch((e: unknown) => {
                if (e instanceof AxiosError) {
                  setLoading(false);
                  ErrorMessage(
                    e?.response?.data?.error?.message ?? "Something went wrong"
                  );
                } else {
                  setLoading(false);
                  ErrorMessage("Something went wrong");
                }
              });
          }
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const formattedProposals = useMemo(() => {
    return formatProposalsForDisplay(proposalListData || []);
  }, [proposalListData]);

  useEffect(() => {
    if (formattedProposals && formattedProposals.length > 0) {
      const getOldAccept = formattedProposals.find(
        (proposal: Proposal) => proposal.status === "accepted"
      );
      setOldAcceptProposal(getOldAccept || null);
    }
  }, [formattedProposals]);

  const makeProposalCopy = () => {
    if (tableRecord?.id) {
      setLoading(true);
      makeProposalAsCopied(tableRecord?.id)
        .then(() => {
          setLoading(false);
          setTableRecord(null);
          dispatch(
            getProposalListing({ company_id: companyId, deal_id: dealId })
          );
          SuccessMessage("Proposal copy created");
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const items = [
    {
      key: "1",
      label: (
        <Button type="text" className="no-hover" onClick={makeProposalCopy}>
          <Image
            src="/images/potential-deals/make-copy.svg"
            alt="Change"
            preview={false}
          />{" "}
          Make a copy
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Popover
          arrow={false}
          overlayClassName="status_popover"
          content={
            <ul>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("in_progress")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #6454F4",
                      background: "#6454F4",
                    }}
                  />{" "}
                  In progress
                  {tableRecord?.status === "in_progress" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>

              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("approval_pending")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #943AF5",
                      background: "#943AF5",
                    }}
                  />{" "}
                  Pending approval
                  {tableRecord?.status === "approval_pending" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("ready")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #44A6F1",
                      background: "#44A6F1",
                    }}
                  />{" "}
                  Ready
                  {tableRecord?.status === "ready" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("sent")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #E6C300",
                      background: "#E6C300",
                    }}
                  />{" "}
                  Sent
                  {tableRecord?.status === "sent" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("accepted")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #00A07D",
                      background: "#00A07D",
                    }}
                  />{" "}
                  Accepted
                  {tableRecord?.status === "accepted" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("revision")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #F06400",
                      background: "#F06400",
                    }}
                  />{" "}
                  Revision
                  {tableRecord?.status === "revision" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("declined")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #CD1951",
                      background: "#CD1951",
                    }}
                  />{" "}
                  Declined
                  {tableRecord?.status === "declined" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => checkStatus("archived")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #98A3A9",
                      background: "#98A3A9",
                    }}
                  />{" "}
                  Archived
                  {tableRecord?.status === "archived" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
            </ul>
          }
          placement="rightTop"
          getPopupContainer={(trigger) =>
            trigger.parentElement || document.body
          }
        >
          <Button type="text" className="no-hover">
            <Image
              src="/images/potential-deals/change-status.svg"
              alt="Move crew’s schedule"
              preview={false}
            />{" "}
            Change status to ...
          </Button>
        </Popover>
      ),
    },
    {
      key: "3",
      disabled: true,
      label: (
        <Button type="text" className="no-hover" disabled={true}>
          <Image
            src="/images/potential-deals/export-icon.svg"
            alt="Export"
            preview={false}
          />
          Export takeoff measurements
        </Button>
      ),
    },
    {
      key: "4",
      disabled: true,
      label: (
        <Button type="text" className="no-hover" disabled={true}>
          <Image
            src="/images/potential-deals/export-icon.svg"
            alt="Export"
            preview={false}
          />{" "}
          Export materials list
        </Button>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          type="text"
          className="delete-hover"
          onClick={() => {
            setIsDeleteModalOpen(true);
          }}
          icon={<DeleteIcon />}
        >
          Delete
        </Button>
      ),
    },
  ];

  const deleteFunction = () => {
    setLoading(true);
    deleteProposalById(tableRecord?.id as string)
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setTableRecord(null);
        dispatch(
          getProposalListing({ company_id: companyId, deal_id: dealId })
        );
        dispatch(getPotentialDealDetails(dealId));
        SuccessMessage("Proposal deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const deleteProposalPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete <b>{tableRecord?.display_name}</b>?
            <br /> <br />
            This action cannot be undone.
          </>
        }
        btnText="Delete proposal"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const estimateIncompletePopUp = {
    visibility: isEstimateIncomplete,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsEstimateIncomplete(false)}
        heading="Estimate details are incomplete"
        paragraph="There are incomplete fields in the estimate. Make sure everything is complete."
        btnText="Open estimate"
        btnApi={() => {
          navigate(
            `/document-editor/${tableRecord?.id}/${dealId}/${companyId}`
          );
          setIsEstimateIncomplete(false);
        }}
        loading={loading}
        isArchivePopup
      />
    ),
    width: 480,
    onCancel: () => setIsEstimateIncomplete(false),
  };

  const formatRelativeDateTime = (dateInput: string | null): string => {
    if (!dateInput) {
      return ""; // Return empty string or a default like 'N/A' if no date is provided
    }

    const date = dayjs(dateInput); // Convert input to a dayjs object

    // Format the time part (e.g., "10:50 AM")
    const timeFormat = "h:mm A"; // 12-hour format with AM/PM

    if (date.isYesterday()) {
      return `Y'day at ${date.format(timeFormat)}`;
    } else if (date.isToday()) {
      return `Today at ${date.format(timeFormat)}`;
    } else {
      // For any other date (future or older past dates)
      return date.format("MMM DD, YYYY [at] h:mm A"); // e.g., "Jul 14, 2025 at 10:50 AM"
    }
  };

  const handleCardStatusChange = (
    status: string,
    proposalId: string
    // hasDocument?: boolean
  ) => {
    setLoading(true);

    // if (!hasDocument) {
    //   setLoading(false);
    //   setIsEstimateIncomplete(true);
    //   return;
    // }

    if (proposalId) {
      const payload = {
        status,
        deal_id: dealId,
      };
      changeProposalStatus(payload, proposalId)
        .then(() => {
          setLoading(false);
          dispatch(getPotentialDealDetails(dealId));
          dispatch(
            getProposalListing({ company_id: companyId, deal_id: dealId })
          );
          setTableRecord(null);
          SuccessMessage("Status changed");
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const proposalIncompletePopUp = {
    visibility: isProposalIncomplete,
    content: (
      <InfoBox
        onClose={() => setIsProposalIncomplete(false)}
        heading="Proposal can’t be accepted"
        paragraph={
          <>
            The following information must be complete to accept this proposal:
            <br />
            <br />
            <ul style={{ paddingLeft: "20px" }}>
              {!dealsDetailData?.start_date && <li>Contract start date</li>}
              {!dealsDetailData?.end_date && <li>Contract end date</li>}
              {!dealsDetailData?.billing && <li>Billing type</li>}
              {!dealsDetailData?.net_terms && <li>Net terms</li>}
              {/* <li>Mandatory fields in the estimate:</li> */}
            </ul>
          </>
        }
      />
    ),
    width: 480,
    onCancel: () => setIsProposalIncomplete(false),
  };

  const confirmChangeSentPop = {
    visibility: openChangeSentPopover,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setOpenChangeSentPopover(false)}
        heading="Confirm status change"
        paragraph={
          <>
            This proposal was sent. Changing its status will make the proposal
            link unavailable to the client.
            <br />
            <br />
            Are you sure you want to proceed?
          </>
        }
        btnText="Confirm"
        btnApi={() => handleStatusChange(changeStatusText)}
        loading={loading}
        isArchivePopup
      />
    ),
    width: 480,
    onCancel: () => setOpenChangeSentPopover(false),
  };
  const confirmChangeAcceptedPop = {
    visibility: openChangeAcceptedPop,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setOpenChangeAcceptedPop(false)}
        heading="Confirm status change"
        paragraph={
          <>
            The customer has accepted this proposal.
            <br />
            <br />
            Are you sure you want to proceed?
          </>
        }
        btnText="Confirm"
        btnApi={() => handleStatusChange(changeStatusText)}
        loading={loading}
        isArchivePopup
      />
    ),
    width: 480,
    onCancel: () => setOpenChangeAcceptedPop(false),
  };
  const signaturePop = {
    visibility: openSignaturePopover,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setOpenSignaturePopover(false)}
        heading="Accept proposal without a signature"
        paragraph={
          <>
            Make sure the customer has accepted this proposal.
            <br />
            <br />
            If the proposal was signed outside of LandScaling or accepted
            through other means, you can attach the accepted document here. It’s
            optional.
            <br />
            <br />
            <FileDragger
              setChecklistFile={setChecklistFile}
              checklistFile={checklistFile}
            />
          </>
        }
        btnText="Confirm"
        btnApi={() => handleStatusChange(changeStatusText)}
        loading={loading}
        isArchivePopup
      />
    ),
    width: 480,
    onCancel: () => setOpenSignaturePopover(false),
  };
  const replaceOldAcceptedPop = {
    visibility: replaceOldAccepted,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setReplaceOldAccepted(false)}
        heading="Another proposal is already accepted"
        paragraph={
          <>
            The proposal ‘<b>{oldAcceptProposal?.display_name}</b>’ was
            accepted.
            <br />
            <br />
            If you accept ‘<b>{tableRecord?.display_name}</b>’, the status of
            the currently accepted proposal will change to Declined.
            <br />
            <br />
            Do you want to replace the accepted proposal?
          </>
        }
        btnText="Yes, replace"
        btnApi={() => changeOldAcceptedProposal(changeStatusText)}
        loading={loading}
        isArchivePopup
      />
    ),
    width: 480,
    onCancel: () => setReplaceOldAccepted(false),
  };

  const sentFollowUpFnc = () => {
    setLoading(true);
    SuccessMessage("Follow-up email sent to the client");

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <>
      {formattedProposals &&
        formattedProposals?.length > 0 &&
        formattedProposals.map((proposal) => (
          <Card className="proposal_card">
            <div className="proposal_card_header">
              <h4
                style={{
                  display: "flex",
                  gap: "24px",
                  alignItems: "center",
                  fontSize: "20px",
                  color: "#070707",
                  fontWeight: "700",
                }}
              >
                {proposal?.display_name}
                {proposal?.summary && (
                  <Tooltip title={proposal?.summary}>
                    <span
                      style={{
                        backgroundColor: "#FFF9D9",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        fontSize: "14px",
                        lineHeight: "20px",
                        fontWeight: "400",
                        color: "#151718",
                        maxWidth: "450px",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                      className="badge"
                    >
                      {proposal?.summary}
                    </span>
                  </Tooltip>
                )}
              </h4>
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight"
                className="actionButton"
              >
                <Button
                  onClick={(x) => {
                    x.stopPropagation();
                    setTableRecord(proposal);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    padding: "0px",
                    height: "28px",
                    width: "28px",
                    backgroundColor: "transparent",
                    border: "1px solid #DEE5E6",
                    borderRadius: "8px",
                  }}
                  className="only_icon"
                  icon={<MoreOutlined />}
                />
              </Dropdown>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "stretch",
                gap: "16px",
              }}
            >
              <Card
                className="estimate"
                onClick={() =>
                  navigate(
                    `/edit-estimate/${proposal?.id}/${dealId}/${companyId}`
                  )
                }
                style={{
                  cursor: "pointer",
                }}
              >
                <h5
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  Estimate
                  <span
                    style={{
                      fontSize: "12px",
                      lineHeight: "16px",
                      fontWeight: "400",
                      color: "#6C7478",
                    }}
                  >
                    Last edit: {formatRelativeTime(proposal?.modified_date)}
                  </span>
                </h5>
                <Flex gap="small" vertical style={{ marginBottom: "8px" }}>
                  <Progress
                    strokeColor="#00A07D"
                    percent={proposal?.gross_margin_perc || 0}
                    size={["100%", 12]}
                  />
                </Flex>
                <div
                  style={{
                    marginBottom: "2px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "space-between",
                  }}
                >
                  <span> Gross margin: </span>
                  <span className="gross_margin_badge">
                    {Number(
                      proposal?.gross_margin_perc?.toFixed(2)
                    ).toLocaleString()}{" "}
                    %
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "space-between",
                  }}
                >
                  <span> Total amount: </span>
                  <span className="total_amount_badge">
                    $
                    {Number(
                      proposal?.total_amount?.toFixed(2)
                    ).toLocaleString()}
                  </span>
                </div>
              </Card>
              <div
                className="proposal_document"
                onClick={() =>
                  navigate(
                    `/document-editor/${proposal?.id}/${dealId}/${companyId}`
                  )
                }
                style={{ cursor: "pointer" }}
              >
                <div className="titleBar">
                  <img
                    src="/images/potential-deals/proposal_document.svg"
                    alt=""
                  />

                  <h6
                    style={{
                      fontSize: "12px",
                      lineHeight: "16px",
                      fontWeight: "400",
                      color: "#6C7478",
                    }}
                  >
                    Last edit :{formatRelativeDateTime(proposal?.modified_date)}
                  </h6>
                </div>

                <h5>Proposal document</h5>
                <h6
                  style={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontWeight: "400",
                  }}
                >
                  For the customer’s review and signature.
                </h6>
              </div>
              {proposal?.status === "in_progress" && (
                <InProgress
                  proposal={proposal}
                  handleCardStatusChange={handleCardStatusChange}
                  setTableRecord={setTableRecord}
                />
              )}
              {proposal?.status === "approval_pending" && (
                <PendingApproval
                  proposal={proposal}
                  handleCardStatusChange={handleCardStatusChange}
                  setTableRecord={setTableRecord}
                />
              )}
              {proposal?.status === "ready" && (
                <Ready
                  proposal={proposal}
                  dealsDetailData={dealsDetailData}
                  setIsProposalIncomplete={setIsProposalIncomplete}
                />
              )}
              {proposal?.status === "sent" && (
                <Sent
                  proposal={proposal}
                  // handleCardStatusChange={handleCardStatusChange}
                  sentFollowUp={sentFollowUpFnc}
                  setTableRecord={setTableRecord}
                />
              )}
              {proposal?.status === "accepted" && (
                <Accepted
                  proposalId={proposal?.id}
                  dealId={dealId}
                  companyId={companyId}
                />
              )}
              {proposal?.status === "revision" && <Revision />}
              {proposal?.status === "declined" && <Declined />}
              {proposal?.status === "archived" && <Archived />}
            </div>
          </Card>
        ))}

      <PopUp {...deleteProposalPopUp} />
      <PopUp {...estimateIncompletePopUp} />
      <PopUp {...proposalIncompletePopUp} />
      <PopUp {...confirmChangeSentPop} />
      <PopUp {...confirmChangeAcceptedPop} />
      <PopUp {...signaturePop} />
      <PopUp {...replaceOldAcceptedPop} />
    </>
  );
}
